import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Form } from 'antd';
import {
  useCurrentUserQuery,
  usePurchaseRequestByIdQuery,
  usePurchaseRequestItemsQuery,
  useRequestPriceMutation,
} from 'api';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { useNotificationApi } from 'hooks/useNotificationApi';
import { SupplierItem } from './useSuppliers';
import { AttachmentItem } from './useAttachments';

export const useRequestPrice = () => {
  const { id } = useParams() as { id: string };
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const location = useLocation();
  const [isSending, setIsSending] = useState(false);

  const notificationApi = useNotificationApi();
  const { handleSuccess, handleError } = useNotificationMessages();
  const [requestPrice] = useRequestPriceMutation();

  const queryParams = useMemo(() => {
    return new URLSearchParams(location.search);
  }, [location]);

  const itemIds = useMemo(() => {
    return queryParams.get('items')?.split(',') || [];
  }, [queryParams]);

  const [selectedItems, setSelectedItems] = useState<any[]>([]);

  const { data: purchaseRequestDetails = {} } = usePurchaseRequestByIdQuery({ id });
  const brandId = useMemo(() => purchaseRequestDetails.purchaseRequest?.brand?.id, [purchaseRequestDetails]);

  const userId = JSON.parse(localStorage.getItem('userId') || 'null');
  const { data: currentUser = {} } = useCurrentUserQuery({ userId }, { skip: !userId });

  const { data: purchaseRequestItems } = usePurchaseRequestItemsQuery({ purchaseRequestId: id }, { skip: !id });

  useEffect(() => {
    if (purchaseRequestItems && itemIds.length > 0 && !selectedItems?.length) {
      const filteredItems = purchaseRequestItems.filter((item: any) => itemIds.includes(item.id));
      setSelectedItems(filteredItems);
    }
  }, [purchaseRequestItems, itemIds, selectedItems]);

  const generateItemsTable = useCallback(() => {
    if (!selectedItems || selectedItems.length === 0) return '';

    let tableHtml = `
    <table style="width:100%; border-collapse:collapse; margin:15px 0;">
      <thead>
        <tr style="background-color:#f5f5f5;">
          <th style="border:1px solid #ccc; padding:8px; text-align:left;">SKU</th>
          <th style="border:1px solid #ccc; padding:8px; text-align:left;">Description</th>
          <th style="border:1px solid #ccc; padding:8px; text-align:center;">Quantity</th>
          <th style="border:1px solid #ccc; width: 130px; padding:8px; text-align:center;">Price Range</th>
        </tr>
      </thead>
      <tbody>
    `;

    selectedItems.forEach((item) => {
      const priceRange =
        (item?.priceRage?.min || 0) === (item?.priceRage?.max || 0)
          ? `${item?.priceRage?.min || 0}€`
          : `${item?.priceRage?.min || 0}€ - ${item?.priceRage?.max || 0}€`;
      tableHtml += `
        <tr>
          <td style="border:1px solid #ccc; padding:8px;">${item.sku || '-'}</td>
          <td style="border:1px solid #ccc; padding:8px;">${item.description || '-'}</td>
          <td style="border:1px solid #ccc; padding:8px; text-align:center;">${item.quantity || '-'}</td>
          <td style="border:1px solid #ccc; padding:8px; width: 130px; text-align:center;">${priceRange}</td>
        </tr>
      `;
    });

    tableHtml += `
      </tbody>
    </table>
    `;

    return tableHtml;
  }, [selectedItems]);

  const handleSendRequest = useCallback(
    async (
      suppliers: SupplierItem[],
      languageEmailBody: Record<string, any>,
      languageEmailSubject: Record<string, any>,
      attachments: AttachmentItem[]
    ) => {
      if (!currentUser?.email?.trim()) {
        notificationApi({ type: 'error', message: 'Sender email is required' });
        return;
      }

      if (!suppliers?.length) {
        notificationApi({ type: 'error', message: 'At least one supplier must be added' });
        return;
      }

      const missingEmailBodies = suppliers.some((s) => !languageEmailBody[s.languageId]?.trim());
      if (missingEmailBodies) {
        notificationApi({ type: 'error', message: 'Email body is required for each selected language' });
        return;
      }

      setIsSending(true);

      try {
        // Logic for sending the request
        const formData = new FormData();
        formData.append('purchaseRequestId', id);
        formData.append('brandId', brandId);
        formData.append('items', itemIds.join(','));
        formData.append('subject', JSON.stringify(languageEmailSubject));
        formData.append('attachmentType', attachments.map((i) => i.type).join(','));
        formData.append('email', currentUser.email);

        attachments.forEach((i) => {
          formData.append('files', i.file as any, i.file.name);
        });

        formData.append('emailBody', JSON.stringify(languageEmailBody));
        formData.append(
          'suppliers',
          JSON.stringify(
            suppliers?.map((i) => ({
              supplierId: i.supplierId,
              contactPersonId: i.contactPersonId,
              languageId: i.languageId,
            }))
          )
        );

        const response: any = await requestPrice(formData);

        if (response?.data?.success) {
          handleSuccess('Request Price was successfully sent.');
          navigate(`/admin/purchase-request/${id}/general`);
        } else {
          if (response?.error?.data?.data) {
            handleError(response.error);
          } else {
            notificationApi({ type: 'error', message: response?.error?.data?.message || 'Something went wrong!' });
          }
        }
      } catch (err) {
        handleError(err);
      } finally {
        setIsSending(false);
      }
    },
    [currentUser?.email, id, brandId, itemIds, requestPrice, notificationApi, handleSuccess, handleError, navigate]
  );

  const handleCancel = useCallback(() => {
    navigate(`/admin/purchase-request/${id}/general`);
  }, [navigate, id]);

  return {
    id,
    form,
    brandId,
    itemIds,
    selectedItems,
    currentUser,
    isSending,
    handleSendRequest,
    handleCancel,
    generateItemsTable,
  };
};
